import dayjs from 'dayjs';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppointmentsStore } from '@/stores/appointments';
import { useInstitutionsStore } from '@/stores/institutions';
import { useListStore } from '@/stores/list';
import { useMeetingNotificationStore } from '@/stores/meetingNotification';
import { useNotificationsStore } from '@/stores/notifications';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import {
  EKioskRouterNames,
  ENotificationActionType,
  ENotificationCallType,
  ENotificationAppointmentsType,
  ENotificationMeetingType,
  EUserRoles,
  TNotificationCamel,
} from '@/types';
import {
  ESocketListenMessageType,
  ESocketSendMessageType,
} from '@/types/socket';
import {
  applyTimezoneOffset,
  getDateFormat,
  getTimeFormat,
} from '@/utils/convertDate';
import { nameFormat } from '@/utils/valuesFormat';
import { matchPath, useNavigate } from 'react-router-dom';
import { getLinkToLogin } from '@/utils/getLinkToLogin';
import { useCallRequestNotification } from './useCallRequestNotification';

/**
 * Subscribes to the notifications socket channel of the current institution
 * and handles incoming notifications: a logout notification clears the
 * session and redirects to login (except on kiosk logout pages), a chat
 * request from another participant is queued as an incoming meeting, a
 * presenter's call is handed to the calls store, and waiting room / follow-up
 * notifications show a toast. Every notification except an incoming meeting
 * and a call is stored in the notifications store.
 * Inactive for guests and for users without a role in the institution.
 * @returns appointment notifications, the notifications panel open state and
 * its change handler, which marks all notifications as read on close.
 */
export const useNotificationsGateway = () => {
  const navigate = useNavigate();
  const { userInstitutionRoleId, userData, clearStates } = useUsersStore(
    (state) => ({
      userInstitutionRoleId: state.roleId ?? '',
      userData: state.userData,
      clearStates: state.clearStates,
    }),
  );

  const currentInstitutionId = useInstitutionsStore(
    (state) => state.currentInstitution?.id,
  );

  const { sendMessage, listenMessage, stopListenMessage, isConnected } =
    useSocketStore((state) => ({
      sendMessage: state.sendMessage,
      listenMessage: state.listenMessage,
      stopListenMessage: state.stopListenMessage,
      isConnected: state.isConnected,
    }));

  const addMeeting = useMeetingNotificationStore((state) => state.addMeeting);
  const timezones = useListStore((state) => state.timezones);
  const role = useUsersStore((state) => state.role);

  const setToast = useToastStore((state) => state.setToast);
  const onCallRequest = useCallRequestNotification();
  const { t } = useTranslation();

  const getAppointmentById = useAppointmentsStore(
    (state) => state.getAppointmentById,
  );
  const [notificationOpen, setNotificationOpen] = useState(false);
  const { readAllNotifications, notifications, setNotification } =
    useNotificationsStore((state) => ({
      notifications: state.getAppointmentNotifications(),
      readAllNotifications: state.readAllNotifications,
      setNotification: state.setNotification,
    }));
  const handleOpenChange = (value: boolean) => {
    if (!value) {
      void readAllNotifications();
    }
    setNotificationOpen(value);
  };

  useEffect(() => {
    // NOTE: Don't subscribe if the user is a guest or the user doesn't have any role in the institution
    if (!role || role === EUserRoles.GUEST) return;
    if (!isConnected || !currentInstitutionId) return;

    sendMessage({
      type: ESocketSendMessageType.NOTIFICATION_START,
      body: { institutionId: currentInstitutionId },
    });

    return () => {
      sendMessage({
        type: ESocketSendMessageType.NOTIFICATION_STOP,
        body: { institutionId: currentInstitutionId },
      });
    };
  }, [currentInstitutionId, isConnected, role, sendMessage]);

  const checkKioskLogoutRoutes = () => {
    const urls = [
      `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGOUT_NOTICE}`,
      `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__UNAVAILABLE_NOTICE}`,
      EKioskRouterNames.KIOSK,
    ];
    return urls.some((url) => !!matchPath(url, location.pathname));
  };

  const handleNotificationMessageCallback = useCallback(
    async (notification: TNotificationCamel) => {
      if (notification.type === ENotificationActionType.LOGOUT) {
        clearStates();
        // this is needed so socket logout message doesn't override
        // original navigation logic for kiosk pages
        if (!checkKioskLogoutRoutes()) {
          navigate(getLinkToLogin());
        }
      }
      if (
        notification.type === ENotificationMeetingType.CHAT_REQUESTED &&
        notification.meta.chat.participants[0].userInstitutionRoleId !==
          userInstitutionRoleId
      ) {
        addMeeting({
          chatId: notification.meta.chat.id,
          withEncounter: notification.meta.chat.withEncounter,
          participants: notification.meta.chat.participants,
        });
        return;
      }
      if (notification.type === ENotificationCallType.CALL_REQUEST) {
        void onCallRequest(notification.meta.callRequest);
        return;
      }
      if (
        notification.type === ENotificationAppointmentsType.WAITING_ROOM_JOINED
      ) {
        const appointment = getAppointmentById(notification.meta.appointmentId);
        if (appointment?.waitingRoom?.endedAt) return;
        const user = appointment?.participants[0].user;
        const date = dayjs(
          applyTimezoneOffset(
            timezones,
            appointment?.start,
            userData?.timezone,
          ),
        );
        setToast({
          title: t('notification-waiting-room-joined', {
            name:
              user?.firstName || user?.lastName
                ? nameFormat(user.firstName, user.lastName)
                : t('patient'),
            date: getDateFormat(date),
            time: getTimeFormat(date),
          }),
          variant: 'info',
        });
      }
      if (
        notification.type ===
        ENotificationAppointmentsType.APPOINTMENT_FOLLOW_UP_REQUESTED
      ) {
        setToast({ title: t('notification-follow-up-requested') });
      }
      setNotification(notification);
    },
    [
      addMeeting,
      getAppointmentById,
      clearStates,
      navigate,
      onCallRequest,
      setNotification,
      setToast,
      t,
      timezones,
      userData?.timezone,
      userInstitutionRoleId,
    ],
  );

  useEffect(() => {
    // NOTE: Don't subscribe if the user is a guest or the user doesn't have any role in the institution
    if (!role || role === EUserRoles.GUEST) return;
    if (!isConnected) return;

    listenMessage({
      type: ESocketListenMessageType.NOTIFICATION,
      callback: handleNotificationMessageCallback,
    });

    return () => stopListenMessage(ESocketListenMessageType.NOTIFICATION);
  }, [
    handleNotificationMessageCallback,
    isConnected,
    listenMessage,
    role,
    stopListenMessage,
  ]);
  return {
    notifications,
    setNotificationOpen,
    notificationOpen,
    handleOpenChange,
  };
};
